import StatusTag from "../../components/common/StatusTag";
import H3 from "../typography/H3";
import SimpleSpinner from "../../components/loader";
import { GetImages } from "../../utils/GetImages";

const FAILURE_REASON_FALLBACK =
  "No connected payment account, or the transfer was declined.";

export type PaymentStatusKind = "pending" | "success" | "failure";

export interface PaymentStatusItem {
  id: number | string;
  reasonLabel?: string;
}

interface PaymentStatusCardProps {
  status: PaymentStatusKind;
  /**
   * Failed/settled lines -- reason text per item is only used for "failure". Required for
   * "success" too (drives the singular/plural heading and gates the render -- an empty array
   * renders nothing, even for "success"). Unused for "pending".
   */
  items?: PaymentStatusItem[];
  /**
   * Shows a close (X) button plus a "Close" text link when provided. Never rendered for
   * "pending" -- a transfer still confirming with the bank can't be meaningfully dismissed
   * mid-poll.
   */
  onDismiss?: () => void;
}

const STATUS_ICON: Record<"success" | "failure", string> = {
  success: GetImages.success,
  failure: GetImages.serverErrorIcon,
};

const DismissButton = ({ onDismiss }: { onDismiss: () => void }) => (
  <button
    type="button"
    className="payment-status-card-dismiss"
    onClick={onDismiss}
    aria-label="Dismiss"
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18 6L6 18M6 6L18 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </button>
);

/**
 * Shared payment-outcome card -- pending/confirming, success, or failure -- shown as a
 * page-level overlay by both settle screens (PayablesPostPayment/ReceivablesPostPayment) so
 * both render the identical card instead of each hand-rolling their own.
 */
const PaymentStatusCard = ({ status, items = [], onDismiss }: PaymentStatusCardProps) => {
  const canDismiss = status !== "pending" && !!onDismiss;

  if (status === "pending") {
    return (
      <div className="empty-state-card-border payment-status-card">
        <div className="d-flex flex-column align-items-center justify-content-center gap-2">
          <div className="emptyState-icon-circle">
            <SimpleSpinner myClassName="Icon40" />
          </div>
          <H3 text="Confirming with your bank" />
          <div className="emptyState-bodytext mt-2">
            <p className="empty-state-bodytext">
              This usually takes just a few seconds — please don't close this window.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  const isFailure = status === "failure";
  const heading = isFailure
    ? items.length === 1
      ? "Payment couldn't be completed"
      : "Some payments couldn't be completed"
    : items.length === 1
      ? "Payment successful"
      : "Payments successful";

  return (
    <div className="empty-state-card-border payment-status-card">
      {canDismiss && <DismissButton onDismiss={onDismiss as () => void} />}
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 w-100">
        <div className="emptyState-icon-circle">
          <img
            src={STATUS_ICON[status]}
            alt=""
            // color-white-icon flattens a fully-opaque multi-tone icon to a blank white disc in
            // dark mode (brightness(0) invert(1) has nothing left to invert against) -- GetImages
            // .success is exactly that shape (see EmptyState's own preserveIconColors escape
            // hatch for the same failure mode), so only the failure icon (line-art, inverts
            // correctly) gets the filter.
            className={`image-icon${isFailure ? " color-white-icon" : ""}`}
          />
        </div>
        <H3 text={heading} />
        {isFailure && (
          <div className="d-flex flex-column align-items-center gap-2 mt-2">
            {items.map((item) => (
              <StatusTag
                key={item.id}
                color="red"
                statusText={item.reasonLabel || FAILURE_REASON_FALLBACK}
                isLarge={false}
                isLong={true}
              />
            ))}
          </div>
        )}
        {canDismiss && (
          <button
            type="button"
            className="view-more-btn payment-status-card-close-text"
            onClick={onDismiss}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentStatusCard;
